/**
 * Online-only API Client
 * This is a simplified version of the offline-capable API client that's been refactored
 * to be online-only. It maintains API compatibility with the original offline API client
 * but simply passes through to the standard API client without offline capabilities.
 */
import api, { directApi } from './api';
import logger from '../utils/logger';

// Select the appropriate API instance based on environment
const apiInstance = process.env.REACT_APP_API_URL ? directApi : api;

// Create an API wrapper that maintains compatibility with the old offlineApi
const offlineApi = {
  /**
   * Check if offline mode is enabled
   * In the online-only version, this always returns false
   * @returns {Promise<boolean>} - Always returns false in online-only mode
   */
  isOfflineModeEnabled: async () => {
    // Log for debugging purposes
    const isActuallyOffline = !navigator.onLine;
    if (isActuallyOffline) {
      logger.debug('OfflineApi', 'Device appears to be offline, but app is in online-only mode');
    }
    
    // Always return false - we're in online-only mode
    return false;
  },

  /**
   * Enable or disable offline mode
   * In the online-only version, this is a no-op that always returns false
   * @param {boolean} enabled - Whether to enable offline mode (ignored in online-only mode)
   * @returns {Promise<boolean>} - Always returns false in online-only mode
   */
  setOfflineMode: async (enabled) => {
    // Log the attempted operation for debugging
    logger.debug('OfflineApi', `Attempted to set offline mode to ${!!enabled}, but app is in online-only mode`);
    
    if (!!enabled) {
      logger.warn('OfflineApi', 'Offline mode is not supported in this version of the application');
    }
    
    // Force online status check - even though we're online-only, this helps update UI components
    // that might be listening for online/offline events
    window.dispatchEvent(new Event('online'));
    
    // Always return false - we're in online-only mode
    return false;
  },
  
  /**
   * Get offline data from a specific store
   * In online-only mode, this always returns an empty array
   * @param {string} storeName - The store name (e.g. 'BOXES', 'ITEMS')
   * @returns {Promise<Array>} - Empty array in online-only mode
   */
  getOfflineData: async (storeName) => {
    logger.debug('OfflineApi', `getOfflineData called for store: ${storeName}`);
    // In online-only mode, we don't have offline data
    return [];
  },
  
  /**
   * Queue a request for later execution when online
   * In online-only mode, this warns that the operation is not supported
   * @param {Object} request - Request object with method, url, data, etc.
   * @returns {Promise<Object>} - An error object in online-only mode
   */
  queueRequest: async (request) => {
    const method = request.method || 'unknown';
    const url = request.url || request.endpoint || 'unknown';
    
    logger.warn('OfflineApi', `Attempted to queue ${method.toUpperCase()} ${url} but offline queueing is not supported`);
    
    return {
      id: null,
      timestamp: Date.now(),
      status: 'error',
      error: 'Offline operations not supported in online-only mode',
      endpoint: request.endpoint || request.url,
      url: request.url || request.endpoint
    };
  },
  
  /**
   * Get a specific box by ID from offline storage
   * In online-only mode, this always returns null
   * @param {string|number} boxId - The box ID
   * @returns {Promise<Object|null>} - Always returns null in online-only mode
   */
  getOfflineBox: async (boxId) => {
    logger.debug('OfflineApi', `getOfflineBox called for box ID: ${boxId}`);
    // In online-only mode, we don't have offline storage
    return null;
  },
  
  /**
   * Get items in a specific box from offline storage
   * In online-only mode, this always returns an empty array
   * @param {string|number} boxId - The box ID
   * @returns {Promise<Array>} - Always returns empty array in online-only mode
   */
  getOfflineBoxItems: async (boxId) => {
    logger.debug('OfflineApi', `getOfflineBoxItems called for box ID: ${boxId}`);
    // In online-only mode, we don't have offline storage
    return [];
  },
  
  /**
   * Update an entity in offline storage
   * In online-only mode, this is a no-op that always returns false
   * @param {string} storeName - The store name (e.g. 'BOXES', 'ITEMS')
   * @param {string|number} id - The entity ID
   * @param {Object} updates - The updates to apply
   * @returns {Promise<boolean>} - Always returns false in online-only mode
   */
  updateOfflineEntity: async (storeName, id, updates) => {
    logger.debug('OfflineApi', `updateOfflineEntity called for ${storeName} ID ${id}`);
    // In online-only mode, we don't update offline entities
    return false;
  },
  
  /**
   * Prefetch essential data for offline use
   * In online-only mode, this is a no-op that always returns true
   * @returns {Promise<boolean>} - Always returns true in online-only mode
   */
  prefetchEssentialData: async () => {
    logger.debug('OfflineApi', 'prefetchEssentialData called but offline storage is not supported');
    return true;
  },
  
  /**
   * Sync the offline database with the server
   * In online-only mode, this is a no-op that always returns true
   * @returns {Promise<boolean>} - Always returns true in online-only mode
   */
  syncWithServer: async () => {
    logger.debug('OfflineApi', 'syncWithServer called but offline storage is not supported');
    return true;
  },
  
  /**
   * Force sync with server with option to prioritize delete operations
   * In online-only mode, this is a no-op that always returns true
   * @param {boolean} prioritizeDeletes - Whether to prioritize delete operations (ignored in online-only mode)
   * @returns {Promise<boolean>} - Always returns true in online-only mode
   */
  forceSyncWithServer: async (prioritizeDeletes = false) => {
    logger.debug('OfflineApi', `forceSyncWithServer called with prioritizeDeletes=${prioritizeDeletes}`);
    return true;
  },
  
  /**
   * Helper method to determine which API implementation to use
   * In online-only mode, this always returns the regular online API client
   * @returns {Promise<Object>} - The API client to use (always the standard API in online-only mode)
   */
  getApi: async () => {
    // Always return the appropriate API client based on environment
    return apiInstance;
  },
  
  /**
   * HTTP GET request
   * In online-only mode, this simply passes through to the standard api.get method
   * @param {string} endpoint - API endpoint to call
   * @param {Object} options - Additional options for the request (ignored in online-only mode)
   * @returns {Promise<Object>} - The API response
   */
  get: async (endpoint, options = {}) => {
    // Smart cache-busting: Only add cache-busting when explicitly requested
    const isImageEndpoint = endpoint.includes('/files/') || endpoint.includes('/images/') || endpoint.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
    
    // Check if cache-busting is explicitly requested via options
    const shouldCacheBust = options && options._cacheBust;
    
    let finalEndpoint = endpoint;
    if (!isImageEndpoint && shouldCacheBust) {
      // Add cache-busting parameter only when explicitly requested
      const cacheBuster = `_t=${Date.now()}`;
      const separator = endpoint.includes('?') ? '&' : '?';
      finalEndpoint = `${endpoint}${separator}${cacheBuster}`;
      logger.debug('OfflineApi', `GET ${endpoint} (with cache-busting)`);
    } else {
      logger.debug('OfflineApi', `GET ${endpoint}`);
    }
    
    return await apiInstance.get(finalEndpoint);
  },
  
  /**
   * HTTP POST request
   * In online-only mode, this simply passes through to the standard api.post method
   * @param {string} endpoint - API endpoint to call
   * @param {Object} data - Data to send with the request
   * @param {Object} options - Additional options for the request (ignored in online-only mode)
   * @returns {Promise<Object>} - The API response
   */
  post: async (endpoint, data = {}, options = {}) => {
    logger.debug('OfflineApi', `POST ${endpoint}`);
    return await apiInstance.post(endpoint, data);
  },
  
  /**
   * HTTP PUT request
   * In online-only mode, this simply passes through to the standard api.put method
   * @param {string} endpoint - API endpoint to call
   * @param {Object} data - Data to send with the request
   * @param {Object} options - Additional options for the request (ignored in online-only mode)
   * @returns {Promise<Object>} - The API response
   */
  put: async (endpoint, data = {}, options = {}) => {
    // Add a cache-busting parameter to prevent stale data on subsequent GETs
    const cacheBuster = `_t=${Date.now()}`;
    const separator = endpoint.includes('?') ? '&' : '?';
    const endpointWithCache = `${endpoint}${separator}${cacheBuster}`;
    
    logger.debug('OfflineApi', `PUT ${endpoint}`);
    return await apiInstance.put(endpointWithCache, data);
  },
  
  /**
   * HTTP DELETE request
   * In online-only mode, this simply passes through to the standard api.delete method
   * @param {string} endpoint - API endpoint to call
   * @param {Object} options - Additional options for the request (ignored in online-only mode)
   * @returns {Promise<Object>} - The API response
   */
  delete: async (endpoint, options = {}) => {
    logger.debug('OfflineApi', `DELETE ${endpoint}`);
    return await apiInstance.delete(endpoint);
  },
  
  /**
   * Get all boxes from online API
   * @returns {Promise<Array>} - The boxes
   */
  getAllBoxes: async () => {
    try {
      logger.debug('OfflineApi', 'getAllBoxes');
      const response = await offlineApi.get('/boxes');
      return response.data || [];
    } catch (error) {
      logger.error('OfflineApi', 'Error in getAllBoxes:', error);
      return [];
    }
  },
  
  /**
   * Get all items from online API
   * @returns {Promise<Array>} - The items
   */
  getAllItems: async () => {
    try {
      logger.debug('OfflineApi', 'getAllItems');
      const response = await offlineApi.get('/items');
      return response.data || [];
    } catch (error) {
      logger.error('OfflineApi', 'Error in getAllItems:', error);
      return [];
    }
  },
  
  /**
   * Get a box by ID from online API
   * @param {string|number} boxId - The box ID
   * @returns {Promise<Object|null>} - The box or null if not found
   */
  getBoxById: async (boxId) => {
    try {
      logger.debug('OfflineApi', `getBoxById: ${boxId}`);
      const response = await offlineApi.get(`/boxes/${boxId}`);
      return response.data || null;
    } catch (error) {
      logger.error('OfflineApi', `Error in getBoxById(${boxId}):`, error);
      return null;
    }
  },
  
  /**
   * Get an item by ID from online API
   * @param {string|number} itemId - The item ID
   * @returns {Promise<Object|null>} - The item or null if not found
   */
  getItemById: async (itemId) => {
    try {
      logger.debug('OfflineApi', `getItemById: ${itemId}`);
      const response = await offlineApi.get(`/items/${itemId}`);
      return response.data || null;
    } catch (error) {
      logger.error('OfflineApi', `Error in getItemById(${itemId}):`, error);
      return null;
    }
  },
  
  /**
   * Get items in a specific box from online API
   * @param {string|number} boxId - The box ID
   * @returns {Promise<Array>} - The items in the box
   */
  getBoxItems: async (boxId) => {
    try {
      logger.debug('OfflineApi', `getBoxItems: ${boxId}`);
      // Use the correct endpoint that filters items by box_id
      const response = await offlineApi.get(`/items?box_id=${boxId}`);
      return response.data || [];
    } catch (error) {
      logger.error('OfflineApi', `Error in getBoxItems(${boxId}):`, error);
      return [];
    }
  },

  /**
   * Get items in a specific box from online API (alias for getBoxItems)
   * @param {string|number} boxId - The box ID
   * @returns {Promise<Array>} - The items in the box
   */
  getItemsByBoxId: async (boxId) => {
    // Alias for getBoxItems for backward compatibility
    return offlineApi.getBoxItems(boxId);
  },

  /**
   * Get offline statistics
   * In online-only mode, this returns empty stats
   * @returns {Promise<Object>} - Empty stats object in online-only mode
   */
  getOfflineStats: async () => {
    logger.debug('OfflineApi', 'getOfflineStats called');
    return {
      totalItems: 0,
      totalBoxes: 0,
      cachedImages: 0,
      pendingOperations: 0,
      lastSync: null,
      storageUsed: 0
    };
  },

  /**
   * Get pending operations
   * In online-only mode, this always returns an empty array
   * @returns {Promise<Array>} - Empty array in online-only mode
   */
  getPendingOperations: async () => {
    logger.debug('OfflineApi', 'getPendingOperations called');
    return [];
  },

  /**
   * Clear offline data
   * In online-only mode, this is a no-op that always returns true
   * @returns {Promise<boolean>} - Always returns true in online-only mode
   */
  clearOfflineData: async () => {
    logger.debug('OfflineApi', 'clearOfflineData called');
    return true;
  },

  /**
   * Retry a failed operation
   * In online-only mode, this is a no-op that always returns true
   * @param {string|number} id - The operation ID (ignored in online-only mode)
   * @returns {Promise<boolean>} - Always returns true in online-only mode
   */
  retryOperation: async (id) => {
    logger.debug('OfflineApi', `retryOperation called for ID ${id}`);
    return true;
  },

  /**
   * Delete a pending operation
   * In online-only mode, this is a no-op that always returns true
   * @param {string|number} id - The operation ID (ignored in online-only mode)
   * @returns {Promise<boolean>} - Always returns true in online-only mode
   */
  deleteOperation: async (id) => {
    logger.debug('OfflineApi', `deleteOperation called for ID ${id}`);
    return true;
  }
};

export default offlineApi;
